// context/ThemeContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {},
  setLightTheme: () => {},
  setDarkTheme: () => {},
  setSystemTheme: () => {},
  isDark: false,
  isLight: true,
  mounted: false
});

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light'); // 'light', 'dark', or 'system'
  const [mounted, setMounted] = useState(false); // Tracks if component mounted on client

  // Initialize theme from localStorage, default to light mode
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('fenkparet-theme');

      // Always default to light mode unless user has explicitly chosen dark mode
      let initialTheme = 'light';
      if (savedTheme) {
        initialTheme = savedTheme;
      }

      setTheme(initialTheme);
      setMounted(true);
    }
  }, []);

  // Apply 'dark' class to document.documentElement and save preference
  useEffect(() => {
    if (mounted) {
      const root = document.documentElement;

      // Remove any existing theme classes
      root.classList.remove('light', 'dark');

      // Determine the effective theme to apply the class
      const isActuallyDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

      if (isActuallyDark) {
        root.classList.add('dark');
      } else {
        root.classList.add('light');
      }

      localStorage.setItem('fenkparet-theme', theme);
    }
  }, [theme, mounted]);

  // System theme listener removed - users explicitly choose light or dark mode

  const toggleTheme = useCallback(() => {
    setTheme(prev => (prev === 'light' ? 'dark' : 'light'));
  }, []);
  const setLightTheme = useCallback(() => setTheme('light'), []);
  const setDarkTheme = useCallback(() => setTheme('dark'), []);
  const setSystemTheme = useCallback(() => setTheme('system'), []);

  // Derive isDark directly from state so it's reactive (DOM class is only set after the effect)
  const isDark = mounted && (
    theme === 'dark' ||
    (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  );

  const value = {
    theme,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    setSystemTheme,
    isDark,
    isLight: !isDark,
    mounted
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  // Handle SSR/SSG where React context might not be available
  if (typeof window === 'undefined') {
    return {
      theme: 'light',
      toggleTheme: () => {},
      setLightTheme: () => {},
      setDarkTheme: () => {},
      setSystemTheme: () => {},
      isDark: false,
      isLight: true,
      mounted: false
    };
  }

  const context = useContext(ThemeContext);
  if (!context || context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
